/**
 * CombatDuck — pure smooth-ramp duck multiplier for combat-intensity music mixing.
 *
 * No Phaser, no Howler import — fully testable in Node/Vitest.
 * MusicDirectorAdapter reads `multiplier` each frame and applies it on top of
 * the user's music-volume setting (via AudioBus.getEffectiveVolume).
 *
 * Ramp model: linear interpolation over rampMs from the current multiplier to
 * the target. Interrupting a ramp (e.g. boss defeated mid-duck) continues from
 * wherever the ramp currently is — no jump.
 */

/** Duck to this fraction of normal volume while boss is active (~35% duck). */
export const DEFAULT_COMBAT_DUCK_MULTIPLIER = 0.65;
/** Linear ramp duration in milliseconds. */
export const DEFAULT_COMBAT_DUCK_RAMP_MS = 250;

export class CombatDuck {
  private readonly duckedTarget: number;
  private readonly rampMs: number;

  /** Current interpolated multiplier (1.0 = no duck). */
  private _current = 1.0;
  /** Start value of the active ramp. */
  private _start = 1.0;
  /** End value of the active ramp (1.0 or duckedTarget). */
  private _target = 1.0;
  /** Elapsed ms into the current ramp. */
  private _elapsed = 0;

  constructor(
    duckMultiplier = DEFAULT_COMBAT_DUCK_MULTIPLIER,
    rampMs = DEFAULT_COMBAT_DUCK_RAMP_MS,
  ) {
    this.duckedTarget = duckMultiplier;
    this.rampMs = rampMs;
  }

  /** Current multiplier — apply to bus effective volume each frame. */
  get multiplier(): number {
    return this._current;
  }

  /** Begin ramping down to the ducked level (call on boss triggered). */
  startDuck(): void {
    if (this._target === this.duckedTarget && this._elapsed === 0) return;
    this._start = this._current;
    this._target = this.duckedTarget;
    this._elapsed = 0;
  }

  /** Begin ramping back up to 1.0 (call on boss defeated / duck lifted). */
  stopDuck(): void {
    if (this._target === 1.0 && this._elapsed === 0) return;
    this._start = this._current;
    this._target = 1.0;
    this._elapsed = 0;
  }

  /**
   * Advance the ramp by deltaMs and return the current multiplier.
   * Call once per frame from the adapter's update().
   */
  tick(deltaMs: number): number {
    if (this._current === this._target) return this._current;

    this._elapsed += deltaMs;
    const t = Math.min(this._elapsed / this.rampMs, 1);
    this._current = this._start + (this._target - this._start) * t;

    if (t >= 1) {
      this._current = this._target;
    }

    return this._current;
  }

  /** True when the multiplier is below 1.0 (ducked or ramping). */
  get isActive(): boolean {
    return this._current < 1.0 || this._target < 1.0;
  }
}
